import NextLink from 'next/link'
import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useRouter } from 'next/router'

import { Button } from '@wartek-id/button-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Shimmer } from '@wartek-id/shimmer'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { useToast } from '@wartek-id/toast-v2'

import { PENGELOLAAN_KINERJA_GPK_PEJABAT_PENILAI_KINERJA_PATH } from 'app/KinerjaShared/constants/path'
import {
  API_GET_PERENCANAAN_LIST_QUERY_KEY,
  useApiGetPerencanaanList,
} from 'api/kinerja/gpk/perencanaan'
import { Header } from 'app/KinerjaShared/components/Header'
import { PerencanaanTable } from 'app/KinerjaGpkPage/components/PerencanaanTable'
import { assetUrl } from 'utils/url'
import { useAPIBulkUpdateState } from 'api/kinerja/useAPIKinerja'
import { useGpkTracker } from 'app/KinerjaGpkPage/hooks/use-gpk-tracker'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { PerencanaanTableItem } from 'app/KinerjaGpkPage/components/PerencanaanTable'
import type { KinerjaStatusGpk } from 'types/kinerja-gpk'

const ALLOWED_STATE: KinerjaStatusGpk = 'WAITING_APPROVAL_PPK'

export function BulkApprovalPerencanaan() {
  const { trackEvent } = useGpkTracker()
  const { periodYear } = usePeriodContext()

  // -----------------------------------------
  // Get perencanaan list data
  // -----------------------------------------
  const {
    data: perencanaanListData,
    isFetching,
    isSuccess: isSuccessPerencanaanList,
  } = useApiGetPerencanaanList({
    params: {
      // To improve data fetching efficiency, we'll fetch all states at once and use it in other pages.
      // Filtering will be handled client-side.
      states: ['ALL'],
    },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
      useErrorBoundary: true,
    },
  })

  const perencanaanItems: Array<PerencanaanTableItem> = useMemo(() => {
    if (!Array.isArray(perencanaanListData?.data)) return []

    // Only show submitted but not approved perencanaan
    const allowedToCheckItems = perencanaanListData.data.filter(
      (item) => item.state === ALLOWED_STATE
    )

    return allowedToCheckItems.map((item) => ({
      id: item.id,
      name: item.nama,
      status: item.state,
      ptkId: item.ptkId,
    }))
  }, [perencanaanListData])

  const totalItems = perencanaanItems.length ?? 0

  // -----------------------------------------
  // Handle events tracking
  // -----------------------------------------
  // Track visit event
  useEffect(() => {
    if (isSuccessPerencanaanList) {
      trackEvent({
        activityType: 'view_daftar_perencanaan_kinerja_sekaligus',
        screenName: 'perencanaan_ppk',
        attributes: {
          year: String(periodYear),
        },
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessPerencanaanList])

  return (
    <main className="w-full max-w-7xl mx-auto py-8 px-6 min-h-screen">
      <Header
        title="Daftar Perencanaan Kinerja"
        subtitle="Setujui Sekaligus"
        className="mb-10"
      />

      <section className="shadow-card rounded-lg bg-white border border-disabled">
        <div className="p-6">
          {isFetching ? (
            <Shimmer height="78px" width="100%" />
          ) : (
            <div className="flex justify-between items-center py-3 px-4 bg-surface-selected-default border border-interactive-default rounded-[4px]">
              <div className="space-y-1">
                <Text variant="heading-md" as="h3" color="primary">
                  {totalItems} Perencanaan dapat langsung Anda setujui sekaligus
                </Text>
                <Text color="subdued">
                  Perencanaan sudah diperiksa oleh Tim Kinerja.
                </Text>
              </div>

              <BulkApprovalAction
                skpIds={perencanaanItems.map((item) => item.id)}
                ptkIds={perencanaanItems.map((item) => item.ptkId)}
              />
            </div>
          )}
        </div>

        <PerencanaanTable
          items={perencanaanItems}
          statusFilter={[ALLOWED_STATE]}
          emptyStateMessage="Tidak ada data perencanaan"
          isLoading={isFetching}
          renderAction={(item) => {
            return (
              <NextLink
                href={`${PENGELOLAAN_KINERJA_GPK_PEJABAT_PENILAI_KINERJA_PATH}/perencanaan/setujui-sekaligus/${item.ptkId}`}
                passHref
              >
                <Button color="black" size="sm" as="a" type={undefined}>
                  Periksa
                </Button>
              </NextLink>
            )
          }}
        />
      </section>
    </main>
  )
}

export type SendActionProps = {
  skpIds: Array<string>
  ptkIds?: Array<string>
}

const CONFIRMATION_TEXT = 'SAYA SETUJUI PERENCANAAN SEKALIGUS'
const SUCCESS_MESSAGE = 'Perencanaan disetujui!'
const ERROR_MESSAGE = 'Gagal setujui perencanaan sekaligus. Coba lagi.'
const LOADING_MESSAGE = 'Menyetujui Perencanaan...'

export function BulkApprovalAction({ skpIds, ptkIds }: SendActionProps) {
  const toast = useToast()
  const { trackEvent } = useGpkTracker()
  const { periodYear } = usePeriodContext()

  const [step, setStep] = useState<
    'CONFIRMATION' | 'LOADING' | 'SUCCESS' | 'IDLE'
  >('IDLE')

  // -----------------------------------------
  // Handle events tracking
  // -----------------------------------------
  const handleTrackApprovalAction = () => {
    trackEvent({
      activityType: 'approve_sekaligus',
      screenName: 'perencanaan_ppk',
      attributes: {
        year: String(periodYear),
        ptk_id_target: ptkIds?.join(','),
      },
    })
  }

  // -------------------------------------
  // Handle submit mutation
  // -------------------------------------
  const mutation = useAPIBulkUpdateState()
  const successCount = mutation.data?.data?.successCount ?? 0

  const handleSubmit = () => {
    setStep('LOADING')

    mutation.mutate({ skpIds, tipe: 'GPK' })
  }

  const handleClose = () => {
    setStep('IDLE')
  }

  const handleSubmitFinished = () => {
    if (mutation.isSuccess) {
      // The bulk mutation allows partial failure,
      // So we need to check the success count
      if (successCount > 0) {
        setStep('SUCCESS')

        handleTrackApprovalAction()
      } else {
        toast({
          message: ERROR_MESSAGE,
        })
        handleClose()
      }
    } else if (mutation.isError) {
      toast({
        message: ERROR_MESSAGE,
      })
      handleClose()
    }
  }

  // -------------------------------------
  // Handle success mutation
  // -------------------------------------
  const queryClient = useQueryClient()
  const router = useRouter()
  const handleSuccess = () => {
    // Invalidate cache
    queryClient.invalidateQueries([API_GET_PERENCANAAN_LIST_QUERY_KEY])

    // Invoke success callback if any
    router.replace(
      `${PENGELOLAAN_KINERJA_GPK_PEJABAT_PENILAI_KINERJA_PATH}/perencanaan/disetujui`
    )
  }

  return (
    <>
      <Button
        color="blue"
        size="lg"
        className="min-w-32"
        disabled={skpIds.length === 0}
        onClick={() => setStep('CONFIRMATION')}
      >
        Setujui ({skpIds.length})
      </Button>

      <ConfirmationDialog
        isOpen={step === 'CONFIRMATION'}
        onPrev={handleClose}
        onNext={handleSubmit}
        confirmationText={CONFIRMATION_TEXT}
        title={`Setujui ${skpIds.length} Perencanaan sekaligus?`}
        description={
          <>
            <strong>Perencanaan tidak dapat diubah lagi</strong>. Semua data
            Perencanaan akan otomatis dikirim ke E-Kinerja dan Guru Pendidikan
            Khusus dapat langsung mulai Pelaksanaan.
          </>
        }
      />

      <LoadingDialog
        isOpen={step === 'LOADING'}
        minDuration={3000}
        onNext={handleSubmitFinished}
        loadingMessage={LOADING_MESSAGE}
        isFinished={mutation.isSuccess || mutation.isError}
      />

      <SuccessDialog
        successMessage={SUCCESS_MESSAGE}
        isOpen={step === 'SUCCESS'}
        count={successCount}
        onNext={handleSuccess}
      />
    </>
  )
}

type ConfirmationDialogProps = {
  isOpen: boolean
  confirmationText: string
  title: string | React.ReactNode
  description: string | React.ReactNode
  onPrev: () => void
  onNext: () => void
}
function ConfirmationDialog({
  isOpen,
  confirmationText,
  title,
  description,
  onPrev,
  onNext,
}: ConfirmationDialogProps) {
  const [confirmation, setConfirmation] = useState('')

  const isEnabled = confirmation === confirmationText

  return (
    <Dialog
      isOpen={isOpen}
      closeOnOverlayClick={false}
      size="sm"
      onClose={onPrev}
    >
      <DialogOverlay />
      <DialogContent
        aria-labelledby="kirim-pegawai-confirmation-dialog-title"
        aria-describedby="kirim-pegawai-confirmation-dialog-description"
      >
        <div className="p-6 space-y-4">
          <img
            src={assetUrl('/illustrations/img-collect-files-folder.png')}
            alt=""
            width={84}
            height={84}
          />

          <Text
            variant="display-sm"
            as="h2"
            id="kirim-pegawai-confirmation-dialog-title"
          >
            {title}
          </Text>

          <Text
            variant="body-lg"
            color="subdued"
            id="kirim-pegawai-confirmation-dialog-description"
          >
            {description}
          </Text>
          <Text variant="body" color="subdued">
            Mohon ketik kalimat persetujuan di bawah ini:
          </Text>
          <div className="bg-blue-5 text-blue-60 p-1 rounded select-none">
            <Text variant="heading-sm" color="inherit">
              {confirmationText}
            </Text>
          </div>
          <div className="space-y-1">
            <Text as="label" htmlFor="uncompleted-confirmation-text">
              Kalimat persetujuan
            </Text>
            <Textarea
              id="uncompleted-confirmation-text"
              placeholder="Ketik di sini."
              autoComplete="off"
              onChange={(e) => setConfirmation(e.target.value)}
            />
          </div>

          <div className="flex space-x-3 !justify-start">
            <Button color="white" size="lg" onClick={onPrev}>
              Kembali
            </Button>
            <Button
              color="blue"
              disabled={!isEnabled}
              size="lg"
              type="button"
              onClick={onNext}
            >
              Setujui
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

type LoadingDialogProps = {
  isOpen: boolean
  minDuration?: number // ms
  onNext: () => void
  loadingMessage: string
  isFinished: boolean
}
function LoadingDialog({
  isOpen,
  minDuration,
  loadingMessage,
  onNext,
  isFinished,
}: LoadingDialogProps) {
  // Flag to indicate minimum loading duration has been reached
  const [reachedMinDuration, setReachedMinDuration] = useState(false)

  // Update flag after some duration
  useEffect(() => {
    let timeout = null
    if (isOpen) {
      // Start the minimum duration timer if dialog is open
      timeout = setTimeout(() => {
        setReachedMinDuration(true)
      }, minDuration)
    } else {
      // Reset flag when dialog is closed
      setReachedMinDuration(false)
    }

    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Invoke onNext only if mutation is finished AND minimum loading duration has been reached
  useEffect(() => {
    if (isFinished && reachedMinDuration) {
      onNext()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFinished, reachedMinDuration])

  return (
    <Dialog isOpen={isOpen} closeOnOverlayClick={false} size="sm">
      <DialogOverlay />
      <DialogContent
        aria-labelledby="kirim-pegawai-loading-dialog-title"
        aria-describedby="kirim-pegawai-loading-dialog-description"
      >
        <div className="pt-20 pb-12 px-6 flex flex-col items-center text-center">
          <span className="mb-9">
            <Spinner size="lg" />
          </span>
          <Text
            variant="display-sm"
            as="h2"
            className="mb-1"
            id="kirim-pegawai-loading-dialog-title"
          >
            {loadingMessage}
          </Text>
          <Text
            variant="body-lg"
            color="subdued"
            id="kirim-pegawai-loading-dialog-description"
          >
            Mohon tunggu sebentar, ya.
          </Text>
        </div>
      </DialogContent>
    </Dialog>
  )
}

type SuccessDialogProps = {
  successMessage: string
  isOpen: boolean
  count: number
  onNext: () => void
}
function SuccessDialog({
  isOpen,
  count,
  successMessage,
  onNext,
}: SuccessDialogProps) {
  return (
    <Dialog isOpen={isOpen} size="sm" closeOnOverlayClick={false}>
      <DialogOverlay />
      <DialogContent
        aria-labelledby="kirim-pegawai-success-dialog-title"
        aria-describedby="kirim-pegawai-success-dialog-description"
      >
        <div className="p-6">
          <img
            src={assetUrl('/illustrations/img-thumbs-up.png')}
            alt=""
            width={84}
            height={84}
            className="mb-4"
          />

          <div className="mb-6">
            <Text
              variant="display-sm"
              as="h2"
              className="mb-2"
              id="kirim-pegawai-success-dialog-title"
            >
              {count} {successMessage}
            </Text>

            <Text
              variant="body-lg"
              color="subdued"
              id="kirim-pegawai-success-dialog-description"
            >
              Anda dapat setujui Perencanaan lain jika ada yang belum disetujui.
              Mohon cek secara berkala.
            </Text>
          </div>

          <Button color="blue" size="lg" type="button" onClick={onNext}>
            Saya mengerti
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
